import axios from 'axios';
import integrationService from './integrationService';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle different error status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - Clear token and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/signin';
          break;
        case 403:
          // Forbidden
          console.error('Access forbidden');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 500:
          // Server error
          console.error('Server error');
          break;
        default:
          console.error('API Error:', error.response.data);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// WhatsApp Business API specific instance


// API Service Methods
export const apiService = {
  // Auth APIs
  auth: {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    logout: () => api.post('/auth/logout'),
    refreshToken: (refreshToken) => api.post('/auth/refresh-token', { refreshToken }),
    getProfile: () => api.get('/auth/profile'),
    updateProfile: (data) => api.put('/auth/profile', data),
    updateOnboardingStep: (data) => api.post('/auth/onboarding-step', data),
    updateNotifications: (data) => api.put('/auth/notifications', data),
    updateSecurity: (data) => api.put('/auth/security', data),
    updateBusinessHours: (data) => api.put('/auth/business-hours', data),
    changePassword: (data) => api.post('/auth/change-password', data),
    getBusinessData: () => api.get('/auth/business-data'),
    updateBusinessData: (data) => api.put('/auth/business-data', data),
    uploadBusinessLogo: (formData) => api.post('/auth/business-data/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    completeOnboarding: (data) => api.post('/auth/complete-onboarding', data || {}),
    checkBusinessRegistration: () => api.get('/auth/check-business-registration'),
    getEmbeddedOnboardingUrl: () => api.get('/auth/embedded-onboarding-url'),
    saveOnboardingStep: (data) => api.post('/auth/save-onboarding-step', data),
    completeOnboardingWithData: (data) => api.post('/auth/complete-onboarding-with-data', data),
  },

  // Customers APIs
  customers: {
    getAll: (params) => api.get('/customers', { params }),
    getById: (id) => api.get(`/customers/${id}`),
    create: (data) => api.post('/customers', data),
    update: (id, data) => api.put(`/customers/${id}`, data),
    delete: (id) => api.delete(`/customers/${id}`),
    import: (contacts) => api.post('/customers/import', { contacts }),
    search: (query) => api.get('/customers/search', { params: { q: query } }),
  },

  // Leads APIs
  leads: {
    getAll: (params) => api.get('/leads', { params }),
    getById: (id) => api.get(`/leads/${id}`),
    create: (data) => api.post('/leads', data),
    update: (id, data) => api.put(`/leads/${id}`, data),
    delete: (id) => api.delete(`/leads/${id}`),
    getStats: () => api.get('/leads/stats'),
  },

  // Templates APIs
  templates: {
    getAll: (params) => api.get('/templates', { params }),
    getById: (id) => api.get(`/templates/${id}`),
    getFromMeta: (metaTemplateId, accessToken) => api.get(`/templates/meta/${metaTemplateId}`, { params: { accessToken } }),
    create: (data) => api.post('/templates', data),
    update: (id, data) => api.put(`/templates/${id}`, data),
    delete: (id) => api.delete(`/templates/${id}`),
    submitToMeta: (id, data) => api.post(`/templates/${id}/submit`, data),
    checkStatus: (id, data) => api.post(`/templates/${id}/check-status`, data),
    sync: (id, data) => api.post(`/templates/${id}/sync`, data),
    syncFromMeta: (data) => api.post('/templates/sync/meta', data),
    duplicate: (id, data) => api.post(`/templates/${id}/duplicate`, data),
    getStats: (businessAccountId) => api.get('/templates/stats', { params: { businessAccountId } }),
    sendTest: (id, data) => api.post(`/templates/${id}/test-send`, data),
  },

  // Promotions APIs
  promotions: {
    getAll: (params) => api.get('/promotions', { params }),
    getById: (id) => api.get(`/promotions/${id}`),
    create: (data) => api.post('/promotions', data),
    update: (id, data) => api.put(`/promotions/${id}`, data),
    delete: (id) => api.delete(`/promotions/${id}`),
    send: (id, recipients) => api.post(`/promotions/${id}/send`, { recipients }),
    pause: (id) => api.post(`/promotions/${id}/pause`),
    resume: (id) => api.post(`/promotions/${id}/resume`),
  },

  // Broadcasts APIs
  broadcasts: {
    getAll: (params) => api.get('/broadcasts', { params }),
    getById: (id) => api.get(`/broadcasts/${id}`),
    create: (data) => api.post('/broadcasts', data),
    update: (id, data) => api.put(`/broadcasts/${id}`, data),
    delete: (id) => api.delete(`/broadcasts/${id}`),
    send: (id, sendNow) => api.post(`/broadcasts/${id}/send`, { sendNow }),
    pause: (id) => api.post(`/broadcasts/${id}/pause`),
    resume: (id) => api.post(`/broadcasts/${id}/resume`),
    getLogs: (id, params) => api.get(`/broadcasts/${id}/logs`, { params }),
    getApprovedTemplates: () => api.get('/broadcasts/approved-templates'),
    getCronStatus: () => api.get('/broadcasts/cron/status'),
    triggerCron: () => api.post('/broadcasts/cron/trigger'),
  },

  // Broadcast Lists APIs
  broadcastLists: {
    getAll: (params) => api.get('/broadcast-lists', { params }),
    getById: (id) => api.get(`/broadcast-lists/${id}`),
    create: (data) => api.post('/broadcast-lists', data),
    update: (id, data) => api.put(`/broadcast-lists/${id}`, data),
    delete: (id) => api.delete(`/broadcast-lists/${id}`),
    addContacts: (id, contacts) => api.post(`/broadcast-lists/${id}/contacts`, { contacts }),
    removeContact: (listId, contactId) => api.delete(`/broadcast-lists/${listId}/contacts/${contactId}`),
  },

  // Invoices APIs
  invoices: {
    getAll: (params) => api.get('/invoices', { params }),
    getById: (id) => api.get(`/invoices/${id}`),
    create: (data) => api.post('/invoices', data),
    update: (id, data) => api.put(`/invoices/${id}`, data),
    delete: (id) => api.delete(`/invoices/${id}`),
    addPayment: (id, paymentData) => api.post(`/invoices/${id}/payment`, paymentData),
    downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
    previewPDF: (id) => api.get(`/invoices/${id}/pdf/preview`, { responseType: 'blob' }),
    send: (id, data) => api.post(`/invoices/${id}/send`, data),
  },

  // Messages APIs
  messages: {
    sendText: (data) => api.post('/messages/send/text', data),
    sendTemplate: (data) => api.post('/messages/send/template', data),
    sendImage: (data) => {
      const formData = new FormData();
      formData.append('phoneNumberId', data.phoneNumberId);
      formData.append('toPhoneNumber', data.toPhoneNumber);
      formData.append('file', data.file);
      if (data.caption) formData.append('caption', data.caption);
      return api.post('/messages/send/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    sendDocument: (data) => {
      const formData = new FormData();
      formData.append('phoneNumberId', data.phoneNumberId);
      formData.append('toPhoneNumber', data.toPhoneNumber);
      formData.append('file', data.file);
      if (data.filename) formData.append('filename', data.filename);
      return api.post('/messages/send/document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    sendVideo: (data) => {
      const formData = new FormData();
      formData.append('phoneNumberId', data.phoneNumberId);
      formData.append('toPhoneNumber', data.toPhoneNumber);
      formData.append('file', data.file);
      if (data.caption) formData.append('caption', data.caption);
      return api.post('/messages/send/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    sendAudio: (data) => {
      const formData = new FormData();
      formData.append('phoneNumberId', data.phoneNumberId);
      formData.append('toPhoneNumber', data.toPhoneNumber);
      formData.append('file', data.file);
      return api.post('/messages/send/audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getConversation: (customerId) => api.get(`/messages/conversations/${customerId}`),
    getConversations: (params) => api.get('/messages/conversations', { params }),
    markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),
    getDeliveryStatus: (params) => api.get('/messages/delivery-status', { params }),
    sendCatalog: (data) => api.post('/messages/send/catalog', data),
    sendCatalogTemplate: (data) => api.post('/messages/send/catalog-template', data),
    sendProductCarousel: (data) => api.post('/messages/send/product-carousel', data),
    sendProductTemplate: (data) => api.post('/messages/send/product-template', data),
    sendImageByUrl: (data) => api.post('/messages/send/image-url', data),
    sendImageTemplate: (data) => api.post('/messages/send/image-template', data),
    sendImageSample: (data) => api.post('/media/sample-image-send', data),
    sendWelcome: (data) => api.post('/messages/send/welcome', data),
    getStatsOverview: (params) => api.get('/messages/stats/overview', { params }),
  },

  

  // Phone Numbers (app-level) - Dynamic API Integration
  phoneNumbers: {
    getByWaba: (params) => api.get(`/phone-numbers/waba`, { params }),
    getById: (id) => api.get(`/phone-numbers/${id}`),
    create: (data) => api.post(`/phone-numbers`, data),
    sync: (wabaId) => wabaId ? api.post(`/phone-numbers/${wabaId}/sync`) : api.post(`/phone-numbers/sync`),
    setDefault: (phoneNumberId, data) => api.post(`/phone-numbers/${phoneNumberId}/set-default`, data),
    getDefault: (wabaId) => api.get(`/phone-numbers/default/${wabaId}`),
    updateStatus: (phoneNumberId, data) => api.put(`/phone-numbers/${phoneNumberId}/status`, data),
    getMetrics: (wabaId) => api.get(`/phone-numbers/metrics/${wabaId}`),
    getStats: (wabaId) => api.get(`/phone-numbers/stats/${wabaId}`),
    requestVerificationCode: (phoneNumberId, data) => api.post(`/phone-numbers/${phoneNumberId}/request-code`, data),
    verifyCode: (phoneNumberId, data) => api.post(`/phone-numbers/${phoneNumberId}/verify-code`, data),
    getMetaInfo: (phoneNumberId, params) => api.get(`/phone-numbers/${phoneNumberId}/meta`, { params }),
    getMessages: (phoneNumberId, params) => api.get(`/phone-numbers/${phoneNumberId}/messages`, { params }),
    register: (phoneNumberId, data = {}) => api.post(`/phone-numbers/${phoneNumberId}/register`, {
      ...data,
      phoneNumberId,
    }),
    phoneNumberRegister: (data = {}) => {
      const phoneNumberId = data.phoneNumberId || data.phone_number_id || data.id || data.phoneNumber;
      const pin = data.pin;

      if (!phoneNumberId) {
        return Promise.reject(new Error('Phone number ID is required'));
      }

      if (!pin || !/^\d{6}$/.test(String(pin))) {
        return Promise.reject(new Error('A valid 6-digit PIN is required'));
      }

      return api.post('/phone-numbers/register', { phoneNumberId, pin });
    },
    delete: (phoneNumberId) => api.delete(`/phone-numbers/${phoneNumberId}`)
  },

  // Webhooks
  webhooks: {
    getAll: () => api.get('/webhooks'),
    create: (data) => api.post('/webhooks', data),
    update: (id, data) => api.put(`/webhooks/${id}`, data),
    delete: (id) => api.delete(`/webhooks/${id}`),
    test: (id) => api.post(`/webhooks/${id}/test`),
    getEvents: (id, params) => api.get(`/webhooks/${id}/events`, { params }),
  },

  // Business Accounts
  businessAccounts: {
    getAll: () => api.get('/business-accounts'),
    getById: (id) => api.get(`/business-accounts/${id}`),
    create: (data) => api.post('/business-accounts', data),
    update: (id, data) => api.put(`/business-accounts/${id}`, data),
    sync: (id) => api.post(`/business-accounts/${id}/sync`),
    getStats: (id) => api.get(`/business-accounts/${id}/stats`),
    delete: (id) => api.delete(`/business-accounts/${id}`),
  },

  // WABA (WhatsApp Business Account)
  waba: {
    getStatus: () => api.get('/waba/status/check'),
    getById: (wabaId) => api.get(`/waba/${wabaId}`),
    requestOtp: (data) => api.post('/waba/request-otp', data),
    verifyOtp: (wabaId, data) => api.post(`/waba/${wabaId}/verify-otp`, data),
    connect: (wabaId, data) => api.post(`/waba/${wabaId}/connect`, data),
    exchangeCode: (data) => api.post('/waba/exchange-code', data),
    getQuality: (wabaId) => api.get(`/waba/${wabaId}/quality`),
    configureWebhook: (wabaId, data) => api.post(`/waba/${wabaId}/webhook`, data),
    disconnect: (wabaId) => api.delete(`/waba/${wabaId}`),
  },

  // Conversations
  conversations: {
    getAll: (params) => api.get('/conversations', { params }),
    getById: (conversationId) => api.get(`/conversations/${conversationId}`),
    getByPhone: (phone) => api.get(`/conversations/phone/${phone}`),
    create: (data) => api.post('/conversations', data),
    update: (conversationId, data) => api.put(`/conversations/${conversationId}`, data),
    markAsRead: (conversationId, data) => api.post(`/conversations/${conversationId}/read`, data),
    delete: (conversationId) => api.delete(`/conversations/${conversationId}`),
    export: (conversationId, data) => api.post(`/conversations/${conversationId}/export`, data),
    getUnreadCount: (params) => api.get('/conversations/unread/count', { params }),
  },

  feedback: {
    getAll: (params) => api.get('/feedback', { params }),
    create: (data) => api.post('/feedback', data),
    update: (id, data) => api.put(`/feedback/${id}`, data),
    delete: (id) => api.delete(`/feedback/${id}`),
  },

  appointments: {
    getAll: (params) => api.get('/appointments', { params }),
    getById: (id) => api.get(`/appointments/${id}`),
    create: (data) => api.post('/appointments', data),
    update: (id, data) => api.put(`/appointments/${id}`, data),
    delete: (id) => api.delete(`/appointments/${id}`),
  },

  // QR Codes
  qrCodes: {
    getAll: (params) => api.get('/qr-codes', { params }),
    getById: (code, params) => api.get(`/qr-codes/${code}`, { params }),
    getCached: (params) => api.get('/qr-codes/cached', { params }),
    getImage: (code, params) => api.get(`/qr-codes/${code}/image`, { params }),
    create: (data) => api.post('/qr-codes', data),
    update: (code, data) => api.put(`/qr-codes/${code}`, data),
    sync: (data) => api.post('/qr-codes/sync', data),
    delete: (code) => api.delete(`/qr-codes/${code}`),
  },

  // Catalogs
  catalogs: {
    getAll: (params) => api.get('/catalogs', { params }),
    getById: (id) => api.get(`/catalogs/${id}`),
    getActive: () => api.get('/catalogs/active'),
    getStats: () => api.get('/catalogs/stats'),
    create: (data) => api.post('/catalogs', data),
    update: (id, data) => api.put(`/catalogs/${id}`, data),
    setActive: (id) => api.post(`/catalogs/${id}/set-active`),
    delete: (id) => api.delete(`/catalogs/${id}`),
    exchangeCode: (data) => api.post('/catalogs/exchange-code', data),
  },

  // Products
  products: {
    getAll: (params) => api.get('/products', { params }),
    getById: (id) => api.get(`/products/${id}`),
    getStats: (params) => api.get('/products/stats', { params }),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
    uploadImage: (formData) => api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  },

  // Business Profiles
  businessProfiles: {
    get: (params) => api.get('/business-profiles', { params }),
    update: (data) => api.post('/business-profiles', data),
    sync: (params) => api.post('/business-profiles/sync', null, { params }),
    createUploadSession: (data) => api.post('/business-profiles/picture/upload-session', data),
    uploadPicture: (formData) => api.post('/business-profiles/picture/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    completePictureUpdate: (data) => api.post('/business-profiles/picture/complete', data),
    delete: (profileId) => api.delete(`/business-profiles/${profileId}`),
  },

  // Media Upload (for templates, messages, etc.)
  media: {
    getAll: (params) => api.get('/media', { params }),
    upload: (file, type) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type); // 'HEADER_IMAGE', 'HEADER_VIDEO', 'HEADER_DOCUMENT'
      return api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getInfo: (filename) => api.get(`/media/${filename}`),
    delete: (filename) => api.delete(`/media/${filename}`),
    sendSample: (data) => api.post('/media/sample-image-send', data),
  },

  // Template Settings (Pre-defined Templates)
  templateSettings: {
    getAvailableDefaults: () => api.get('/template-settings/defaults'),
    getDefault: (templateId) => api.get(`/template-settings/defaults/${templateId}`),
    getPredefinedStatus: () => api.get('/template-settings/predefined-status'),
    getUtilityServiceTemplates: (params) => api.get('/template-settings/utility-service', { params }),
    createUtilityServiceTemplate: (data) => api.post('/template-settings/utility-service', data),
    updateUtilityServiceTemplate: (templateSettingsId, data) => api.put(`/template-settings/utility-service/${templateSettingsId}`, data),
    deleteUtilityServiceTemplate: (templateSettingsId) => api.delete(`/template-settings/utility-service/${templateSettingsId}`),
    getAll: (params) => api.get('/template-settings', { params }),
    getById: (templateSettingsId) => api.get(`/template-settings/${templateSettingsId}`),
    create: (data) => api.post('/template-settings', data),
    apply: (data) => api.post('/template-settings/apply', data),
    refreshStatus: (templateId) => api.post(`/template-settings/refresh-status/${templateId}`),
    update: (templateSettingsId, data) => api.put(`/template-settings/${templateSettingsId}`, data),
    delete: (templateSettingsId) => api.delete(`/template-settings/${templateSettingsId}`),
    recordUsage: (templateSettingsId) => api.post(`/template-settings/${templateSettingsId}/use`),
    sendInvoice: (data) => api.post('/template-settings/send-invoice', data),
    sendFlow: (data) => api.post('/template-settings/send-flow', data),
  },

  // Webhooks
  webhooks: {
    getAll: (params) => api.get('/webhook', { params }),
    create: (data) => api.post('/webhook', data),
    update: (id, data) => api.put(`/webhook/${id}`, data),
    delete: (id) => api.delete(`/webhook/${id}`),
    getLogs: (params) => api.get('/webhook/logs', { params }),
    testMessage: (data) => api.post('/webhook/test/message', data),
    testStatus: (data) => api.post('/webhook/test/status', data),
  },

  // Orders (Commerce)
  orders: {
    getAll: (params) => api.get('/commerce/orders', { params }),
    getById: (id, params) => api.get(`/commerce/orders/${id}`, { params }),
    create: (data) => api.post('/commerce/orders', data),
    updateStatus: (id, data) => api.put(`/commerce/orders/${id}/status`, data),
    updatePayment: (id, data) => api.put(`/commerce/orders/${id}/payment`, data),
    addTracking: (id, data) => api.put(`/commerce/orders/${id}/tracking`, data),
    delete: (id, data) => api.delete(`/commerce/orders/${id}`, { data }),
    getStatistics: (params) => api.get('/commerce/orders/statistics', { params }),
    generateInvoice: (id, data) => api.post(`/commerce/orders/${id}/invoice`, data),
    getInvoice: (id, params) => api.get(`/commerce/orders/${id}/invoice`, { params }),
  },

  // Dashboard APIs
  dashboard: {
    getStats: (params) => api.get('/dashboard/stats', { params }),
    getRecent: (params) => api.get('/dashboard/recent', { params }),
  },

  // Integration APIs
  integrations: {
    getToken: () => integrationService.getToken(),
    overrideWebhook: (data) => integrationService.overrideWebhook(data),
  },
};

export default api;

